import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BYPASS_MSAL } from '@app/core/interceptors/msal-skip.interceptor';
import { environment } from '@environments/environment';
import { firstValueFrom } from 'rxjs';
import {
  OtpCreateRequest,
  OtpCreateResponse,
  OtpValidateRequest,
  OtpValidateResponse,
} from '../../domain/otp.domain';
import { OtpRepository } from '../../domain/otp.repository';

@Injectable()
export class OtpHttpRepository extends OtpRepository {
  private readonly _http = inject(HttpClient);

  /**
   * POST /otps — creates (or regenerates) an OTP.
   * The backend resolves the delivery channel from the whitelist or JWT;
   * the frontend never sends the phone number (CF1 - hardening rule).
   */
  createOtp(request: OtpCreateRequest & { bypassMsal: boolean }): Promise<OtpCreateResponse> {
    const { bypassMsal = true, ...httpRequest } = request;

    const url = bypassMsal ? `${environment.API_URL_BASE}otps/onboarding` : `${environment.API_URL_BASE}otps/users`;

    return firstValueFrom(
      this._http.post<OtpCreateResponse>(url, httpRequest, {
        context: new HttpContext().set(BYPASS_MSAL, bypassMsal),
      })
    );
  }

  /**
   * POST /otps/validate — validates the 6-digit code.
   * Any non-validatable state returns HTTP 400 OTP_INVALID (CC2 transversal rule).
   * On success the backend returns a single-use verificationToken (CC1 / CD1).
   */
  validateOtp(request: OtpValidateRequest, authenticated: boolean): Promise<OtpValidateResponse> {
    const url = authenticated ? `${environment.API_URL_BASE}otps/users/validate` : `${environment.API_URL_BASE}otps/onboarding/validate`;
    
    return firstValueFrom(
      this._http.post<OtpValidateResponse>(url, request, {
        context: new HttpContext().set(BYPASS_MSAL, authenticated),
      })
    );
  }
}
